import {
  convertLength,
  MeasurementInput,
  type ImperialLengthUnit,
  type LengthUnit,
} from "@rothoblaas/design-system";
import type { FC } from "react";

type ImperialLengthInputProps = {
  // Always metric, in metricUnit: the form only ever holds metric values.
  value: number | null;
  onValueChange: (value: number | null) => void;
  onBlur?: () => void;
  metricUnit: LengthUnit;
  imperialUnit: ImperialLengthUnit;
  // Decimals the metric value is rounded to, e.g. 0 for whole mm, 3 for m (to the mm).
  metricDecimalScale: number;
  // Nearest fraction of an inch shown, e.g. 8 for 1/8".
  maxDenominator?: number;
  isInvalid?: boolean;
};

const roundTo = (value: number, decimalScale: number): number => {
  const factor = 10 ** decimalScale;
  return Math.round(value * factor) / factor;
};

// Shows a metric form value in imperial units, and converts what is typed straight back to metric
// (rounded to the field's metric precision), so calculations and saving never see imperial values.
const ImperialLengthInput: FC<ImperialLengthInputProps> = ({
  value,
  onValueChange,
  onBlur,
  metricUnit,
  imperialUnit,
  metricDecimalScale,
  maxDenominator,
  isInvalid,
}) => (
  <MeasurementInput
    system="imperial"
    unit={imperialUnit}
    maxDenominator={maxDenominator}
    value={value == null ? null : convertLength(value, metricUnit, imperialUnit)}
    onValueChange={(imperialValue) =>
      onValueChange(
        imperialValue == null
          ? null
          : roundTo(convertLength(imperialValue, imperialUnit, metricUnit), metricDecimalScale),
      )
    }
    onBlur={onBlur}
    isInvalid={isInvalid}
  />
);

export default ImperialLengthInput;
